import {Group, Paper, Progress, SimpleGrid, Stack, Text, Title, UnstyledButton} from '@mantine/core'
import {IconTruck, IconUsers} from '@tabler/icons-react'

import {GroupedEquipmentAssignmentCard} from 'src/equipment/grouped-equipment-assignment-card'
import {stockSourceKey} from 'src/equipment/stock-source'
import {
    fulfillmentUnfilledProgressColor,
    identityHeaderSubtle,
    identityProgressColor,
} from 'src/theme/semantic-colors'

import type {RentalAsset} from '../assets/rental-assets/schemas'
import type {FulfillmentGroup} from './assignment-grouping-utils'
import {emptySlotCount, fulfillmentAssignedQuantity} from './assignment-grouping-utils'
import {AssignmentSlot} from './assignment-slot'
import {FulfillmentStatusBadge} from './fulfillment-status-badge'

type FulfillmentGroupCardProps = {
    group: FulfillmentGroup
    jobStartDate: Date
    isStockType?: boolean
    firstPartyRentalAssetIds?: ReadonlySet<string>
    rentalAssetsById?: Map<string, RentalAsset>
    onOpenPool?: () => void
    onRemove?: (resourceId: string) => void
    onRemoveStockGroup?: (
        rentalAssetTypeId: string,
        assignedCount: number,
        offeringId?: string,
    ) => void
}

export function FulfillmentGroupCard({
    group,
    jobStartDate,
    isStockType = false,
    firstPartyRentalAssetIds,
    onOpenPool,
    onRemove,
    onRemoveStockGroup,
}: FulfillmentGroupCardProps) {
    const filledCount = fulfillmentAssignedQuantity(group)
    const progressValue = group.needed > 0 ? Math.min(100, (filledCount / group.needed) * 100) : 100
    const unfilledValue = Math.max(0, 100 - progressValue)
    const filledColor = identityProgressColor(group.kind)
    const emptySlots = emptySlotCount(group)
    const headerColor = identityHeaderSubtle(group.kind)
    const icon = group.kind === 'worker' ? <IconUsers size={24} /> : <IconTruck size={24} />

    const showStockLayout = isStockType && group.kind === 'rental_asset'
    const showSingleEmptyAffordance = showStockLayout && emptySlots > 0

    const header = (
        <Group justify="space-between" align="flex-start" wrap="nowrap">
            <Group gap="md" align="flex-start" wrap="nowrap" style={{flex: 1, minWidth: 0}}>
                <Paper withBorder p="sm" radius="md" bg="white">
                    {icon}
                </Paper>
                <Stack gap={4} style={{flex: 1, minWidth: 0}}>
                    <Title order={5}>{group.label}</Title>
                    <Progress.Root size="sm" radius="xl" w="100%" transitionDuration={200}>
                        {progressValue > 0 && (
                            <Progress.Section
                                value={progressValue}
                                color={filledColor}
                                aria-label="Filled"
                            />
                        )}
                        {unfilledValue > 0 && (
                            <Progress.Section
                                value={unfilledValue}
                                color={fulfillmentUnfilledProgressColor()}
                                striped
                                aria-label="Unfilled"
                            />
                        )}
                    </Progress.Root>
                    <Text size="sm" c="dimmed">
                        {filledCount} / {group.needed} filled
                    </Text>
                </Stack>
            </Group>

            <FulfillmentStatusBadge status={group.status} />
        </Group>
    )

    return (
        <Paper withBorder radius="md" style={{overflow: 'hidden'}}>
            {onOpenPool ? (
                <UnstyledButton
                    onClick={onOpenPool}
                    w="100%"
                    style={{display: 'block'}}
                    aria-label={`Manage ${group.label}`}
                >
                    <Paper
                        p="md"
                        bg={headerColor}
                        style={{borderBottom: '1px solid var(--mantine-color-gray-3)'}}
                    >
                        {header}
                    </Paper>
                </UnstyledButton>
            ) : (
                <Paper
                    p="md"
                    bg={headerColor}
                    style={{borderBottom: '1px solid var(--mantine-color-gray-3)'}}
                >
                    {header}
                </Paper>
            )}

            <Paper p="md" bg="gray.0">
                <SimpleGrid cols={{base: 1, sm: 2}} spacing="sm">
                    {showStockLayout
                        ? (group.stockEquipmentGroups ?? []).map((stock) => (
                              <GroupedEquipmentAssignmentCard
                                  key={stockSourceKey(stock)}
                                  resourceName={stock.rentalAssetTypeName}
                                  supplierName={stock.thirdPartyRentalCompanyName ?? undefined}
                                  quantity={stock.assignedQuantity}
                                  onRemove={
                                      onRemoveStockGroup
                                          ? () =>
                                                onRemoveStockGroup(
                                                    stock.rentalAssetTypeId,
                                                    stock.assignedQuantity,
                                                    stock.thirdPartyRentalAssetOfferingId ??
                                                        undefined,
                                                )
                                          : undefined
                                  }
                              />
                          ))
                        : group.assigned.map((assignment, index) => (
                              <AssignmentSlot
                                  key={assignment.workerId ?? assignment.rentalAssetId ?? index}
                                  group={group}
                                  assignment={assignment}
                                  jobStartDate={jobStartDate}
                                  firstPartyRentalAssetIds={firstPartyRentalAssetIds}
                                  onRemove={onRemove}
                              />
                          ))}
                    {showStockLayout && showSingleEmptyAffordance ? (
                        <AssignmentSlot group={group} empty onOpenPool={onOpenPool} />
                    ) : null}
                    {!showStockLayout
                        ? Array.from({length: emptySlots}).map((_, index) => (
                              <AssignmentSlot
                                  key={`empty-${index}`}
                                  group={group}
                                  empty
                                  onOpenPool={onOpenPool}
                              />
                          ))
                        : null}
                </SimpleGrid>
            </Paper>
        </Paper>
    )
}
